// src/hooks/websocket/useWebSocket.ts
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { WebSocketState, WebSocketStatus, WebSocketMessage, WebSocketHandlers, Action } from './types';
import { WS_CONFIG } from './config';
import { useWebSocketConnection } from './useWebSocketConnection';

export const useWebSocket = (handlers?: WebSocketHandlers) => {
  const { user } = useAuth();
  const [state, setState] = useState<WebSocketState>({
    status: WebSocketStatus.CLOSED,
    error: null,
    reconnectAttempt: 0,
    lastReconnectTime: 0,
  });
  const accessToken = localStorage.getItem('authToken');

  const updateState = useCallback((updates: Partial<WebSocketState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleMessage = useCallback(async (event: MessageEvent) => {
    console.log('handleMessage called');
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      
      if (message.data.accountId === user?.accountId) {
        console.log('Received message:', message);
        
        if (Object.values(Action).includes(message.data.type as Action)) {
          const action = message.data.type as Action;
          toast.success(`${message.data.filename} has been ${action.toLowerCase().replace('_', ' ')}`, {
            duration: 3000,
          });
        }
        
        if (handlers?.onFileChange) {
          await handlers.onFileChange();
        }
      }
    } catch (error) {
      console.error('Error processing WebSocket message:', error);
    }
  }, [user?.accountId, handlers]);

  const { wsRef, initializeWebSocket, cleanup } = useWebSocketConnection({
    accessToken,
    updateState,
    handleMessage,
    config: WS_CONFIG
  });

  useEffect(() => {
    initializeWebSocket();
    return () => {
      cleanup();
    };
  }, [cleanup, initializeWebSocket]);

  return {
    sendMessage: useCallback((message: any) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(message));
        return true;
      }
      return false;
    }, [wsRef]),
    status: state.status,
    error: state.error,
    isConnected: state.status === WebSocketStatus.OPEN,
    isReconnecting: state.status === WebSocketStatus.RECONNECTING,
    reconnectAttempt: state.reconnectAttempt
  };
};